import type { Map } from "leaflet";
import {
  getPrintLayout,
  getPxPerMm,
  type MapPrintOptions,
} from "./printLayout";
import { getGlMap } from "./leafletMaplibreInternals";

/**
 * 印刷モード時に地図コンテナを一時的に拡大／復元するためのユーティリティ。
 *
 * 通常 CSS サイズのまま printCanvas へ貼り付けると大きく拡大されてしまうため、
 * キャプチャ前にコンテナを 200dpi 相当へ拡大してタイルを再ロードしておき、
 * 最終 drawImage が拡大方向のリサンプリングを行わないようにする。
 */

export interface MapContainerSizeState {
  prevPosition: string;
  prevWidth: string;
  prevHeight: string;
  prevTop: string;
  prevLeft: string;
  prevZIndex: string;
}

/**
 * 印刷出力に必要な地図コンテナ CSS サイズを算出する。
 *
 * 出力の地図領域 (mm × dpi) を devicePixelRatio で割ったサイズにすることで、
 * createMapCanvas が生成する Canvas が composePrintCanvas の貼り付け先と
 * ぴったり同じ画素数になり、最終的な drawImage が拡大リサンプリングを行わなく
 * なる。
 */
export const getPrintCaptureSize = (
  printOptions: MapPrintOptions,
  devicePixelRatio?: number
): { widthCssPx: number; heightCssPx: number } => {
  const layout = getPrintLayout(printOptions);
  const dpr =
    devicePixelRatio ??
    (typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1);
  if (!Number.isFinite(dpr) || dpr <= 0) {
    throw new RangeError("devicePixelRatio must be a positive finite number");
  }
  const pxPerMm = getPxPerMm(printOptions.dpi);
  return {
    widthCssPx: Math.ceil((layout.mapWidthMm * pxPerMm) / dpr),
    heightCssPx: Math.ceil((layout.mapHeightMm * pxPerMm) / dpr),
  };
};

/**
 * 地図コンテナを画面外に固定配置し、指定サイズへ一時的に拡大する。
 *
 * 副作用: 元の position / left / top / width / height / zIndex を保存して返すので、
 * 呼び出し側は処理終了時に必ず restoreMapContainerSize でロールバックすること。
 */
export const enlargeMapContainerOffscreen = (
  mapInstance: Map,
  widthCssPx: number,
  heightCssPx: number
): MapContainerSizeState => {
  const container = mapInstance.getContainer();
  const state: MapContainerSizeState = {
    prevPosition: container.style.position,
    prevWidth: container.style.width,
    prevHeight: container.style.height,
    prevTop: container.style.top,
    prevLeft: container.style.left,
    prevZIndex: container.style.zIndex,
  };
  // position:fixed で flow から外し、画面外へ追いやる。
  // こうすることで親レイアウトを乱さず、ユーザー視界にも入らない。
  container.style.position = "fixed";
  container.style.left = "-100000px";
  container.style.top = "0";
  container.style.width = `${widthCssPx}px`;
  container.style.height = `${heightCssPx}px`;
  container.style.zIndex = "-1";
  container.setAttribute("data-printing", "true");
  mapInstance.invalidateSize(false);
  // MapLibre レイヤーは独自に viewport を持つので明示的に resize する。
  mapInstance.eachLayer((layer) => {
    const glMap = getGlMap(layer);
    if (glMap) {
      glMap.resize();
      glMap.triggerRepaint();
    }
  });
  return state;
};

export const restoreMapContainerSize = (
  mapInstance: Map,
  state: MapContainerSizeState
): void => {
  const container = mapInstance.getContainer();
  container.style.position = state.prevPosition;
  container.style.width = state.prevWidth;
  container.style.height = state.prevHeight;
  container.style.top = state.prevTop;
  container.style.left = state.prevLeft;
  container.style.zIndex = state.prevZIndex;
  container.removeAttribute("data-printing");
  mapInstance.invalidateSize(false);
  mapInstance.eachLayer((layer) => {
    const glMap = getGlMap(layer);
    if (glMap) {
      glMap.resize();
      glMap.triggerRepaint();
    }
  });
};
